import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';

interface Configuration {
    key: string;
    value: string;
    source: 'database' | 'env';
    updatedAt: string | null;
    updatedBy: string | null;
    description: string | null;
}

interface Props {
    token: string;
}

const getApiBaseUrl = () => {
    let url = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';
    return url.endsWith('/') ? url.slice(0, -1) : url;
};

const API_BASE_URL = getApiBaseUrl();

export default function ConfigurationTab({ token }: Props) {
    const [configs, setConfigs] = useState<Configuration[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchConfigs();
    }, [token]);

    const fetchConfigs = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${API_BASE_URL}/api/saas/configurations`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!response.ok) throw new Error('Failed to fetch configurations');
            const data = await response.json();
            setConfigs(data.data);
        } catch (error) {
            console.error(error);
            toast.error('Failed to load configurations');
        } finally {
            setLoading(false);
        }
    };

    const handleUpdate = async (key: string, value: string) => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/saas/configurations/${key}`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ value })
            });

            if (!response.ok) throw new Error('Failed to update configuration');

            toast.success(`${key} updated`);
            fetchConfigs();
        } catch (error) {
            console.error(error);
            toast.error('Failed to update configuration');
        }
    };

    if (loading) return <div className="p-4 text-center">Loading configurations...</div>;

    return (
        <div>
            <div className="mb-6">
                <h2 className="text-xl font-semibold text-gray-900">System Configuration</h2>
                <p className="text-sm text-gray-500">Manage environment variables and system settings.</p>
            </div>

            <div className="bg-white shadow overflow-hidden sm:rounded-lg border border-gray-200">
                <ul className="divide-y divide-gray-200">
                    {configs.map((config) => (
                        <li key={config.key} className="px-6 py-4 hover:bg-gray-50 transition-colors">
                            <div className="flex items-center justify-between flex-wrap gap-4">
                                <div className="flex-1 min-w-[200px]">
                                    <p className="text-sm font-medium text-indigo-600 truncate">{config.key}</p>
                                    <div className="flex items-center gap-2 mt-1">
                                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${config.source === 'database' ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-800'
                                            }`}>
                                            Source: {config.source}
                                        </span>
                                        {config.updatedAt && (
                                            <span className="text-xs text-gray-400">
                                                Updated: {new Date(config.updatedAt).toLocaleDateString()}
                                            </span>
                                        )}
                                    </div>
                                    {config.key === 'LOG_LEVEL' && (
                                        <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                            Note: A redeployment/restart may be needed for full effect.
                                        </p>
                                    )}
                                    {config.description && <p className="text-sm text-gray-500 mt-1">{config.description}</p>}
                                </div>

                                <div className="flex items-center">
                                    {renderInput(config, handleUpdate)}
                                </div>
                            </div>
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
}

function renderInput(config: Configuration, onUpdate: (k: string, v: string) => void) {
    if (config.key === 'SEND_TO_WHATSAPP' || config.key === 'ENABLE_CAMPAIGN_PROCESSOR') {
        const isTrue = config.value === 'true';
        return (
            <div className="flex items-center gap-3">
                <span className={`text-sm font-medium ${isTrue ? 'text-green-600' : 'text-gray-500'}`}>
                    {isTrue ? 'Active' : 'Inactive'}
                </span>
                <button
                    onClick={() => onUpdate(config.key, isTrue ? 'false' : 'true')}
                    className={`relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${isTrue ? 'bg-green-600' : 'bg-gray-200'
                        }`}
                >
                    <span className="sr-only">Use setting</span>
                    <span
                        aria-hidden="true"
                        className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200 ${isTrue ? 'translate-x-5' : 'translate-x-0'
                            }`}
                    />
                </button>
            </div>
        );
    }

    if (config.key === 'LOG_LEVEL') {
        return (
            <select
                value={config.value}
                onChange={(e) => onUpdate(config.key, e.target.value)}
                className="block w-40 pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
            >
                {['error', 'warn', 'info', 'http', 'verbose', 'debug', 'silly'].map(level => (
                    <option key={level} value={level}>{level}</option>
                ))}
            </select>
        );
    }

    return (
        <div className="flex gap-2">
            <input
                defaultValue={config.value}
                onBlur={(e) => {
                    if (e.target.value !== config.value) onUpdate(config.key, e.target.value);
                }}
                className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md px-3 py-2 border"
            />
        </div>
    );
}
